import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Wallet, Receipt, Truck, Clock, RotateCcw, Download, Ban } from "lucide-react";

import type { RootState, AppDispatch } from "../../../redux/store";
import { listenToFoodBrandFinance } from "../../../redux/foodOrderSlice";

import {
  HEART, InsightsStyles, StatTile, SectionHead, EmptyState, money, fmtDay,
} from "./FreshInsightsKit";

interface FreshFinanceProps { brandId: string }

/* the food slice registers under "FoodOrder"; older stores wired it as
   "foodOrder" — read both so this doesn't depend on the store key */
const foodState = (s: RootState): any =>
  (s as any).FoodOrder ?? (s as any).foodOrder ?? {};

const TXN_STYLE: Record<string, { bg: string; tx: string; Icon: any }> = {
  Sale:      { bg: HEART.greenSoft, tx: HEART.green,     Icon: Receipt   },
  Refund:    { bg: HEART.redSoft,   tx: HEART.redDark,   Icon: RotateCcw },
  Cancelled: { bg: HEART.divider,   tx: HEART.textMuted, Icon: Ban       },
};

/* a dish name or note can carry a comma; quote every cell so the
   spreadsheet doesn't split a row down the middle */
const csvCell = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const FreshFinance: React.FC<FreshFinanceProps> = ({ brandId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const f = useSelector((s: RootState) => foodState(s).brandFinance ?? {});

  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToFoodBrandFinance(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  const txns = (f.transactions ?? []) as any[];

  /* CSV over a print dialog: kitchens reconcile these against a bank
     statement, and a spreadsheet is what that actually needs */
  const exportCsv = () => {
    const head = ["Order", "Type", "Date", "Amount", "Status"].map(csvCell).join(",");
    const body = txns.map((t) =>
      [
        t.id,
        t.type,
        t.at ? new Date(t.at).toISOString().slice(0, 10) : "",
        Number(t.amount ?? 0).toFixed(2),
        t.status,
      ].map(csvCell).join(",")
    );
    const blob = new Blob([[head, ...body].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${brandId}-kitchen-transactions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const feePct = useMemo(
    () => (f.gross ? Math.round((f.fees / f.gross) * 1000) / 10 : 0),
    [f.gross, f.fees]
  );

  return (
    <div className="si">
      <InsightsStyles />

      <SectionHead
        title="Finance"
        right={
          txns.length > 0 ? (
            <button className="si-btn bl" onClick={exportCsv}>
              <Download size={13} /> Export CSV
            </button>
          ) : undefined
        }
      />

      <div className="si-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", marginBottom: 20 }}>
        <StatTile icon={<Receipt size={15} />} label="Gross sales" value={money(f.gross)} delay={0} />
        <StatTile icon={<Wallet size={15} />} label="Platform fee" value={money(f.fees)}
          sub={`${feePct}% of gross`} tone="red" delay={1} />
        <StatTile icon={<Truck size={15} />} label="Delivery collected" value={money(f.delivery)} delay={2} />
        <StatTile icon={<Wallet size={15} />} label="Net earnings" value={money(f.net)} tone="green" delay={3} />
      </div>

      {/* what's actually payable vs still in flight — payouts only
          release once an order is delivered */}
      <div className="si-slab" style={{ padding: "16px 18px", marginBottom: 20 }}>
        <p className="si-eyebrow">WHERE THE MONEY IS</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <Clock size={12} color={HEART.textMuted} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: HEART.textMuted }}>Held until delivered</span>
            </div>
            <div className="si-num" style={{ fontSize: 19 }}>{money(f.pending)}</div>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <Wallet size={12} color={HEART.green} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: HEART.textMuted }}>Cleared for payout</span>
            </div>
            <div className="si-num" style={{ fontSize: 19, color: HEART.green }}>
              {money(Math.max(0, (f.net ?? 0) - (f.pending ?? 0)))}
            </div>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <RotateCcw size={12} color={HEART.redDark} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: HEART.textMuted }}>Refunded</span>
            </div>
            <div className="si-num" style={{ fontSize: 19, color: HEART.redDark }}>{money(f.refunded)}</div>
          </div>
        </div>
      </div>

      {txns.length === 0 ? (
        <EmptyState
          emoji="🧾"
          title="No transactions yet"
          body="Every sale, refund and cancellation from this kitchen will be listed here."
        />
      ) : (
        <div className="si-slab">
          <div className="si-strip" />
          <div style={{ padding: "14px 16px 4px" }}>
            <p className="si-eyebrow">TRANSACTIONS · NEWEST FIRST</p>
          </div>

          {txns.map((t) => {
            const s = TXN_STYLE[t.type] ?? TXN_STYLE.Sale;
            const negative = t.amount < 0;
            return (
              <div key={`${t.id}-${t.type}`} className="si-row">
                <div style={{
                  width: 34, height: 34, borderRadius: 11, flexShrink: 0,
                  border: `2px solid ${HEART.dark}`, background: s.bg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <s.Icon size={14} color={s.tx} strokeWidth={2.2} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 12.5,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    #{String(t.id).slice(0, 14).toUpperCase()}
                  </div>
                  <div style={{ fontSize: 10.5, color: HEART.textMuted, fontWeight: 600, marginTop: 2 }}>
                    {t.type} · {fmtDay(t.at)}
                  </div>
                </div>

                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div className="si-num" style={{
                    fontSize: 14,
                    color: negative ? HEART.redDark : t.type === "Cancelled" ? HEART.textFaint : HEART.green,
                  }}>
                    {negative ? "−" : ""}{money(Math.abs(t.amount))}
                  </div>
                  <span className="si-pill" style={{
                    marginTop: 5,
                    background: t.status === "completed" ? HEART.greenSoft : HEART.blush,
                    color: t.status === "completed" ? HEART.green : HEART.redDark,
                  }}>
                    {t.status === "completed" ? "Cleared" : "Pending"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FreshFinance;
